import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Hub } from 'aws-amplify/utils';
import { checkAuthState } from '../store/authSlice';
import { getAndClearRedirectPath, storeRedirectPathTo } from '../util/redirectUtils';
import { trackLogin, trackSignup } from '../util/tracking';

const OAUTH_AUTH_INTENT_KEY = 'stu_oauth_auth_intent';

const getStoredOAuthAuthIntent = () => {
  try {
    const intent = sessionStorage.getItem(OAUTH_AUTH_INTENT_KEY);
    sessionStorage.removeItem(OAUTH_AUTH_INTENT_KEY);
    return intent === 'signup' ? 'signup' : 'login';
  } catch (e) {
    return 'login';
  }
};

const storeOAuthAuthIntent = (intent) => {
  if (intent !== 'signup' && intent !== 'login') return;

  try {
    sessionStorage.setItem(OAUTH_AUTH_INTENT_KEY, intent);
  } catch (e) {
    // Ignore storage failures; default login tracking still works.
  }
};

const OAuthRedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'customOAuthState': {
          try {
            const raw = payload?.data;
            const decoded = typeof raw === 'string' ? decodeURIComponent(raw) : raw;
            const parsed = typeof decoded === 'string' ? JSON.parse(decoded) : decoded;
            if (parsed?.redirect && typeof parsed.redirect === 'string') {
              storeRedirectPathTo(parsed.redirect);
            }
            if (parsed?.authIntent === 'signup' || parsed?.source === 'social-signup') {
              storeOAuthAuthIntent('signup');
            } else if (parsed?.authIntent === 'login' || parsed?.source === 'social-login') {
              storeOAuthAuthIntent('login');
            }
          } catch (e) {
            // ignore parse errors
          }
          break;
        }
        case 'signInWithRedirect':
          // Check auth state first
          await dispatch(checkAuthState());
          if (getStoredOAuthAuthIntent() === 'signup') {
            trackSignup({
              method: 'oauth',
              source: 'oauth_redirect',
            });
          } else {
            trackLogin({
              method: 'oauth',
              source: 'oauth_redirect',
            });
          }
          
          // Get the stored redirect path and navigate there
          const redirectPath = getAndClearRedirectPath();
          navigate(redirectPath, { replace: true });
          break;
        case 'signInWithRedirect_failure':
          console.error('OAuth sign-in failed:', payload.data);
          // Clear any stored redirect path and go to home
          getAndClearRedirectPath();
          navigate('/');
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, [navigate, location, dispatch]);

  return null; // This component doesn't render anything
};

export default OAuthRedirectHandler;
